import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getOptimizedUrl, ABOUT_IMAGE_MAP } from '../generated-images-config';
import { User } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Sobre <span className="text-gradient">Nosotros</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Conoce nuestra historia, misión y el equipo que hace posible cada proyecto
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Historia</h2>
                <p className="text-gray-600 leading-relaxed">
                  Fundada en 2008, Atomic Corporation nació de la visión de un grupo de ingenieros 
                  especializados que identificaron una necesidad crítica en el sector industrial: proveer soluciones 
                  integrales de alta calidad con un servicio excepcional. Desde nuestros humildes comienzos 
                  en un pequeño taller, hemos crecido hasta convertirnos en un referente nacional en soluciones 
                  industriales.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A lo largo de estos más de 15 años, hemos enfrentado y superado incontables desafíos 
                  técnicos, adaptándonos constantemente a las nuevas tecnologías y necesidades del mercado. 
                  Cada proyecto exitoso ha fortalecido nuestra reputación y expandido nuestra capacidad 
                  para servir mejor a nuestros clientes.
                </p>
              </div>
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Nuestra Misión</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Ser el aliado estratégico preferido de las industrias del país, proporcionando 
                  soluciones innovadoras, confiables y sostenibles que impulsen el crecimiento y 
                  competitividad de nuestros clientes.
                </p>
                <div className="bg-primary-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-primary-900 mb-3">Nuestros Valores</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700"><strong>Excelencia:</strong> Compromiso con la más alta calidad en todo lo que hacemos</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700"><strong>Innovación:</strong> Búsqueda constante de soluciones tecnológicas avanzadas</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700"><strong>Integridad:</strong> Transparencia y honestidad en todas nuestras relaciones</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700"><strong>Compromiso:</strong> Dedicación total con el éxito de nuestros clientes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestro Equipo</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Un equipo multidisciplinario de expertos comprometidos con la excelencia y la innovación
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* CEO & Fundador */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.leader, 112, 112)}
                        alt="Edwin Fernandez - CEO & Fundador"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Edwin Fernandez</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                      </svg>
                      CEO & Fundador
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Ingeniero Químico con 10 años de experiencia en el sector industrial.
                      Líder visionario con pasión por la innovación tecnológica y sostenibilidad.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Liderazgo</span>
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Química</span>
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Sostenibilidad</span>
                    </div>
                  </div>
                </div>

                {/* Directora de Operaciones */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.mariaGarcia, 112, 112)}
                        alt="María García - Directora de Operaciones"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">María García</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Directora de Operaciones
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Especialista en optimización de procesos con 15 años de experiencia.
                      Garantiza la excelencia operativa en cada proyecto.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">Logística</span>
                      <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">Procesos</span>
                      <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">Calidad</span>
                    </div>
                  </div>
                </div>

                {/* Director Técnico */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.engineer, 112, 112)}
                        alt="Carlos Rodríguez - Director Técnico"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Carlos Rodríguez</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                      </svg>
                      Director Técnico
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Experto en soluciones industriales avanzadas. Lidera nuestro equipo de
                      ingenieros y técnicos especializados.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">I+D</span>
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">Ingeniería</span>
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">Tecnología</span>
                    </div>
                  </div>
                </div>

                {/* Asesora de Ventas */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.salesAdvisor, 112, 112)}
                        alt="Luz Espinoza - Asesora de Ventas"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Luz Espinoza</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                      </svg>
                      Asesora de Ventas
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Especialista en atención al cliente y desarrollo de relaciones comerciales.
                      Construye conexiones duraderas con clientes estratégicos.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Ventas</span>
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Atención</span>
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">Relaciones</span>
                    </div>
                  </div>
                </div>

                {/* Jefe de Calidad */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.qualityManager, 112, 112)}
                        alt="Ana Rodríguez - Jefe de Calidad"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ana Rodríguez</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Jefe de Calidad
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Experta en sistemas de gestión de calidad ISO 9001.
                      Garantiza que cada producto cumpla con los más altos estándares.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">ISO 9001</span>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Auditorías</span>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Mejora Continua</span>
                    </div>
                  </div>
                </div>

                {/* Jefe de Proyectos */}
                <div className="team-card bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="w-28 h-28 mx-auto mb-6 overflow-hidden rounded-full shadow-lg">
                      <img
                        src={getOptimizedUrl(ABOUT_IMAGE_MAP.projectManager, 112, 112)}
                        alt="Pedro Fernández - Jefe de Proyectos"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Pedro Fernández</h3>
                    <p className="text-primary-600 font-semibold mb-4 flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Jefe de Proyectos
                    </p>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      Certificación PMP con 10 años gestionando proyectos complejos.
                      Asegura entregas puntuales y dentro del presupuesto.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">PMP</span>
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">PMO</span>
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Metodologías</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estadísticas del equipo */}
              <div className="mt-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Nuestro Equipo en Números</h3>
                  <p className="text-gray-600">La experiencia combinada que nos respalda</p>
                </div>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600 mb-2">62+</div>
                    <div className="text-gray-600 font-medium">Años Combinados</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-secondary-600 mb-2">25+</div>
                    <div className="text-gray-600 font-medium">Proyectos Activos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent-600 mb-2">98%</div>
                    <div className="text-gray-600 font-medium">Satisfacción Cliente</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600 mb-2">12+</div>
                    <div className="text-gray-600 font-medium">Certificaciones</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 mb-16">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Nuestros Logros</h2>
                <div className="grid md:grid-cols-4 gap-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">500+</div>
                    <div className="text-gray-600">Clientes Satisfechos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">15+</div>
                    <div className="text-gray-600">Años de Experiencia</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">1000+</div>
                    <div className="text-gray-600">Proyectos Completados</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">24/7</div>
                    <div className="text-gray-600">Soporte Técnico</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Compromiso con el Futuro</h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
                En Atomic Corporation, no solo miramos hacia el presente, sino que estamos comprometidos 
                con el futuro de la industria en nuestro país. Continuamos invirtendo en tecnología, 
                capacitando a nuestro equipo y expandiendo nuestras capacidades para seguir siendo 
                el socio estratégico que las industrias necesitan para prosperar en un mundo cada vez más competitivo.
              </p>
              <div className="bg-secondary-900 text-white rounded-xl p-8 max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold mb-4">¿Listo para trabajar juntos?</h3>
                <p className="mb-6">
                  Descubra cómo nuestras soluciones pueden transformar sus operaciones industriales.
                </p>
                <button 
                  onClick={() => window.location.href = '/contact'}
                  className="btn-primary px-8 py-3 text-lg"
                >
                  Contáctanos Hoy
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;